import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, Plus, Minus, Pencil, Trash2, AlertTriangle } from "@/components/icons";
import { DeleteConfirm } from "@/components/DeleteConfirm";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page } from "@/components/PageConsole";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState, InlineEmpty } from "@/components/ListState";
import { useUnsavedGuard, DiscardDialog } from "@/components/useUnsavedGuard";
import { formatEuro } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { MoneyInput } from "@/components/MoneyInput";
import { parseAmount, amountToInput, firstAmountError } from "@/lib/money";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const emptyForm = {
  id: null,
  name: "",
  sku: "",
  order_number: "",
  storage_location_id: "__none__",
  current_stock: 0,
  min_stock: 0,
  max_stock: "",
  unit: "stuks",
  supplier: "",
  price_ex_vat: "",
  linked_categories: [],
};
const num = (v) => (v === "" || v == null ? null : Number(v));
const money = (v) => parseAmount(v).value;

export default function PartsStock() {
  const { canWrite } = useAuth();
  const writable = canWrite();
  const [searchParams, setSearchParams] = useSearchParams();
  const [parts, setParts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [snapshot, setSnapshot] = useState("");
  const partGuard = useUnsavedGuard(JSON.stringify(form) !== snapshot, () => setOpen(false));

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [p, l, c] = await Promise.all([
        api.get("/parts"),
        api.get("/locations"),
        api.get("/asset-categories"),
      ]);
      setParts(p.data);
      setLocations(l.data);
      setCategories(c.data);
    } catch (e) {
      setLoadError(getErrorMessage(e, "Kan onderdelen niet laden."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const locMap = useMemo(() => Object.fromEntries(locations.map((l) => [l.location_id, l.name])), [locations]);
  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.category_id, c.name])), [categories]);
  // Storage locations preferred; fall back to all if none are typed 'opslag'.
  const storageLocations = useMemo(() => {
    const opslag = locations.filter((l) => l.type === "opslag");
    return opslag.length ? opslag : locations;
  }, [locations]);

  const lowCount = parts.filter((p) => p.low_stock).length;
  const negativeCount = parts.filter((p) => p.negative_stock).length;
  // A count below zero is a bin that has to be counted by hand one day. It is a
  // quiet fact, never an alarm colour, and never a reorder signal.
  const [onlyNegative, setOnlyNegative] = useState(false);
  // §8: the dashboard tile "Onderdelen onder minimum" opens this list already
  // filtered — the count and its list are one action away, not two.
  const [onlyLow, setOnlyLow] = useState(searchParams.get("onder") === "1");
  const clearOnlyLow = () => {
    setOnlyLow(false);
    const p = Object.fromEntries(searchParams);
    delete p.onder;
    setSearchParams(p, { replace: true });
  };
  const shown = onlyLow
    ? parts.filter((p) => p.low_stock)
    : (onlyNegative ? parts.filter((p) => p.negative_stock) : parts);

  const startCreate = () => { setForm(emptyForm); setSnapshot(JSON.stringify(emptyForm)); setError(""); setOpen(true); };
  const startEdit = (p) => {
    const f = {
      id: p.id,
      name: p.name || "",
      sku: p.sku || "",
      order_number: p.order_number || "",
      storage_location_id: p.storage_location_id || "__none__",
      current_stock: p.current_stock ?? 0,
      min_stock: p.min_stock ?? 0,
      max_stock: p.max_stock ?? "",
      unit: p.unit || "stuks",
      supplier: p.supplier || "",
      price_ex_vat: amountToInput(p.price_ex_vat),
      linked_categories: p.linked_categories || [],
    };
    setForm(f); setSnapshot(JSON.stringify(f));
    setError(""); setOpen(true);
  };

  const submit = async () => {
    const amountError = firstAmountError([form.price_ex_vat]);
    if (amountError) { toast.error(amountError); return; }
    setError("");
    if (!form.name.trim()) { setError("Geef het onderdeel een naam."); return; }
    setSaving(true);
    const body = {
      name: form.name.trim(),
      sku: form.sku.trim(),
      order_number: (form.order_number || "").trim(),
      storage_location_id: form.storage_location_id === "__none__" ? null : form.storage_location_id,
      current_stock: Number(form.current_stock) || 0,
      min_stock: Number(form.min_stock) || 0,
      max_stock: num(form.max_stock),
      unit: form.unit || "stuks",
      supplier: form.supplier.trim(),
      price_ex_vat: money(form.price_ex_vat),
      linked_categories: form.linked_categories,
    };
    try {
      if (form.id) await api.put(`/parts/${form.id}`, body);
      else await api.post("/parts", body);
      toast.success(form.id ? "Onderdeel bijgewerkt" : "Onderdeel toegevoegd");
      setOpen(false);
      await load();
    } catch (e) {
      setError(getErrorMessage(e, "Opslaan mislukt."));
    } finally { setSaving(false); }
  };

  const adjust = async (p, delta) => {
    setBusyId(p.id);
    try {
      const { data } = await api.post(`/parts/${p.id}/adjust`, { delta });
      setParts((prev) => prev.map((x) => (x.id === p.id ? data : x)));
      if (data.low_stock) toast.warning(`${data.name}: onder minimum (${data.current_stock}/${data.min_stock})`);
    } catch (e) {
      toast.error(getErrorMessage(e, "Bijwerken mislukt."));
    } finally { setBusyId(null); }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/parts/${p.id}`);
      toast.success("Onderdeel verwijderd");
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Verwijderen mislukt.")); }
  };

  const toggleCat = (id) => setForm((f) => ({
    ...f,
    linked_categories: f.linked_categories.includes(id)
      ? f.linked_categories.filter((x) => x !== id)
      : [...f.linked_categories, id],
  }));

  const facts = [`${parts.length} onderdelen`,
    ...(lowCount ? [`${lowCount} onder minimum`] : []),
    ...(negativeCount ? [
      <button
        key="negatief"
        type="button"
        data-testid="fact-negatieve-telling"
        className="underline decoration-dotted hover:text-white"
        onClick={() => setOnlyNegative((v) => !v)}
      >
        {negativeCount} met negatieve telling
      </button>,
    ] : [])];
  const actions = writable
    ? [{ key: "nieuw", label: "Onderdeel toevoegen", icon: Plus, variant: "primary",
         testid: "part-new-button", onClick: startCreate }]
    : [];

  return (
    <Page console={{ variant: "view", title: "Onderdelen", facts, actions }}>
      <div className="space-y-4">

      {lowCount > 0 && (
        <Card className="flex flex-wrap items-center justify-between gap-2 border-letop-rand bg-letop-vlak p-3 text-body text-letop-tekst" data-testid="parts-low-banner">
          <span className="flex items-center gap-2">
            <AlertTriangle size={18} /> {lowCount === 1 ? "1 onderdeel is" : `${lowCount} onderdelen zijn`} onder het minimum — tijd om bij te bestellen.
          </span>
          {onlyLow ? (
            <Button variant="secondary" size="sm" data-testid="parts-low-filter-clear" onClick={clearOnlyLow}>Alles tonen</Button>
          ) : (
            <Button variant="outline" size="sm" data-testid="parts-low-filter-only" onClick={() => setOnlyLow(true)}>Alleen deze tonen</Button>
          )}
        </Card>
      )}

      {onlyLow && lowCount === 0 && (
        <Card className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid="parts-low-filter-empty">
          <span className="text-body text-foreground">Geen onderdelen onder het minimum. De dashboard-tegel liet een teller zien; die is inmiddels weggewerkt.</span>
          <Button variant="secondary" size="sm" data-testid="parts-low-filter-empty-clear" onClick={clearOnlyLow}>Alles tonen</Button>
        </Card>
      )}

      {onlyNegative && (
        <Card className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid="parts-negative-filter">
          <span className="text-body text-foreground">
            Alleen onderdelen met een telling onder nul. Deze bins moeten geteld worden; ze zijn geen bestelsignaal.
          </span>
          <Button variant="secondary" size="sm" data-testid="parts-negative-filter-clear" onClick={() => setOnlyNegative(false)}>
            Alles tonen
          </Button>
        </Card>
      )}

      {loadError ? (
        <ErrorState testid="parts-load-error" message={loadError} onRetry={load} />
      ) : (
      <Card className="p-5">
        {!loading && parts.length === 0 ? (
          <InlineEmpty
            testid="parts-empty"
            title="Nog geen onderdelen in voorraad"
            purpose="Hier houd je bij wat er op de schappen ligt: aantal, minimum en leverancier. Bij een reparatie boekt het systeem het verbruik er meteen af en waarschuwt het als je onder je minimum komt."
            action={writable ? { label: "Onderdeel toevoegen", icon: Plus, onClick: startCreate } : null}
          />
        ) : (
        <>
        <div className="hidden sm:block">
          <Table data-testid="parts-table">
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Opslag</TableHead>
                <TableHead className="text-center">Voorraad</TableHead>
                <TableHead className="text-center">Min/Max</TableHead>
                <TableHead>Leverancier</TableHead>
                <TableHead className="text-right">Prijs</TableHead>
                {writable && <TableHead className="text-right">Acties</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={writable ? 8 : 7} className="py-6 text-center text-body text-muted-foreground">Laden...</TableCell></TableRow>}
              {!loading && shown.map((p) => (
                <TableRow key={p.id} data-testid={`part-row-${p.id}`} className={p.low_stock ? "bg-letop-vlak" : ""}>
                  <TableCell className="text-body font-medium">
                    {p.name}
                    {p.low_stock && <Badge className="ml-2 border-fout-rand bg-fout-vlak text-fout-tekst" data-testid={`part-low-badge-${p.id}`}>Laag</Badge>}
                    {p.linked_categories?.length ? (
                      <div className="mt-0.5 text-data text-muted-foreground">{p.linked_categories.map((c) => catMap[c] || c).join(", ")}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-body text-muted-foreground">{p.sku}</TableCell>
                  <TableCell className="text-body">{p.storage_location_id ? (locMap[p.storage_location_id] || p.storage_location_id) : ""}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      {writable && (
                        <Button variant="outline" size="icon" className="h-7 w-7" data-testid={`part-minus-${p.id}`} disabled={busyId === p.id} onClick={() => adjust(p, -1)}><Minus size={14} /></Button>
                      )}
                      <span className={`w-8 text-center text-body font-semibold tabular-nums ${p.low_stock ? "text-fout-tekst" : "text-foreground"}`} data-testid={`part-stock-${p.id}`}>{p.current_stock}</span>
                      {writable && (
                        <Button variant="outline" size="icon" className="h-7 w-7" data-testid={`part-plus-${p.id}`} disabled={busyId === p.id} onClick={() => adjust(p, 1)}><Plus size={14} /></Button>
                      )}
                    </div>
                    <div className="mt-0.5 text-center text-data text-muted-foreground">{p.unit}</div>
                  </TableCell>
                  <TableCell className="text-center text-body tabular-nums">{p.min_stock ?? 0}{p.max_stock != null ? ` / ${p.max_stock}` : ""}</TableCell>
                  <TableCell className="text-body">{p.supplier}</TableCell>
                  <TableCell className="text-right text-body tabular-nums">{p.price_ex_vat != null ? formatEuro(p.price_ex_vat) : ""}</TableCell>
                  {writable && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`part-edit-${p.id}`} onClick={() => startEdit(p)}><Pencil size={18} /></Button>
                        <DeleteConfirm
                          trigger={<Button variant="ghost" size="icon" data-testid={`part-delete-${p.id}`}><Trash2 size={18} /></Button>}
                          title={`Onderdeel "${p.name}" verwijderen?`}
                          description="Dit kan niet ongedaan gemaakt worden."
                          onConfirm={() => remove(p)}
                          testid={`part-delete-confirm-${p.id}`}
                        />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile: stacked cards (naam → opslag → laag-badge, with +/- adjust). */}
        <div className="divide-y divide-border sm:hidden" data-testid="parts-cardlist">
          {loading && <p className="py-6 text-center text-body text-muted-foreground">Laden...</p>}
          {!loading && shown.map((p) => (
            <div key={p.id} data-testid={`part-card-${p.id}`} className={`space-y-2 p-3 ${p.low_stock ? "bg-letop-vlak" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{p.name}</span>
                    {p.low_stock && <Badge className="border-fout-rand bg-fout-vlak text-fout-tekst" data-testid={`part-card-low-badge-${p.id}`}>Laag</Badge>}
                  </div>
                  <div className="text-data text-muted-foreground">{p.storage_location_id ? (locMap[p.storage_location_id] || p.storage_location_id) : "Geen opslaglocatie"}</div>
                  <div className="text-data text-muted-foreground">
                    {p.sku ? `${p.sku} · ` : ""}{p.supplier || "geen leverancier"}{p.price_ex_vat != null ? ` · ${formatEuro(p.price_ex_vat)}` : ""}{p.order_number ? ` · bestelnr. ${p.order_number}` : ""}
                  </div>
                  {p.linked_categories?.length ? (
                    <div className="text-data text-muted-foreground">{p.linked_categories.map((c) => catMap[c] || c).join(", ")}</div>
                  ) : null}
                </div>
                {writable && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="icon" data-testid={`part-card-edit-${p.id}`} onClick={() => startEdit(p)}><Pencil size={18} /></Button>
                    <DeleteConfirm
                      trigger={<Button variant="ghost" size="icon" data-testid={`part-card-delete-${p.id}`}><Trash2 size={18} /></Button>}
                      title={`Onderdeel "${p.name}" verwijderen?`}
                      description="Dit kan niet ongedaan gemaakt worden."
                      onConfirm={() => remove(p)}
                      testid={`part-card-delete-confirm-${p.id}`}
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-data text-muted-foreground">Min/Max: {p.min_stock ?? 0}{p.max_stock != null ? ` / ${p.max_stock}` : ""}</span>
                <div className="flex items-center gap-2">
                  {writable && <Button variant="outline" size="icon" className="h-9 w-9" data-testid={`part-card-minus-${p.id}`} disabled={busyId === p.id} onClick={() => adjust(p, -1)}><Minus size={18} /></Button>}
                  <span className={`w-10 text-center text-body font-semibold tabular-nums ${p.low_stock ? "text-fout-tekst" : "text-foreground"}`} data-testid={`part-card-stock-${p.id}`}>{p.current_stock}</span>
                  {writable && <Button variant="outline" size="icon" className="h-9 w-9" data-testid={`part-card-plus-${p.id}`} disabled={busyId === p.id} onClick={() => adjust(p, 1)}><Plus size={18} /></Button>}
                  <span className="w-10 text-data text-muted-foreground">{p.unit}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
        )}
      </Card>
      )}

      <Dialog open={open} onOpenChange={partGuard.requestClose}>
        <DialogContent className="max-w-2xl" srDescription="Gegevens van dit onderdeel: naam, artikelnummer, opslaglocatie en voorraad.">
          <DiscardDialog guard={partGuard} />
          <DialogHeader><DialogTitle>{form.id ? "Onderdeel bewerken" : "Onderdeel toevoegen"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Naam</Label><Input data-testid="part-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>SKU / artikelnr.</Label><Input data-testid="part-sku-input" value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} /></div>
            {/* The number you order it by; without it a part cannot be ordered. */}
            <div className="space-y-1.5"><Label>Bestelnummer</Label><Input data-testid="part-order-number-input" value={form.order_number} onChange={(e) => setForm((f) => ({ ...f, order_number: e.target.value }))} /></div>
            <div className="space-y-1.5">
              <Label>Opslaglocatie</Label>
              <Select value={form.storage_location_id} onValueChange={(v) => setForm((f) => ({ ...f, storage_location_id: v }))}>
                <SelectTrigger data-testid="part-location-select"><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Geen</SelectItem>
                  {storageLocations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Huidige voorraad</Label><Input type="number" data-testid="part-current-input" value={form.current_stock} onChange={(e) => setForm((f) => ({ ...f, current_stock: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Eenheid</Label><Input data-testid="part-unit-input" value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} placeholder="stuks, liter, ..." /></div>
            <div className="space-y-1.5"><Label>Minimum voorraad</Label><Input type="number" data-testid="part-min-input" value={form.min_stock} onChange={(e) => setForm((f) => ({ ...f, min_stock: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Maximum voorraad (optioneel)</Label><Input type="number" data-testid="part-max-input" value={form.max_stock} onChange={(e) => setForm((f) => ({ ...f, max_stock: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Leverancier</Label><Input data-testid="part-supplier-input" value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Prijs (excl. btw)</Label><MoneyInput testid="part-price-input" value={form.price_ex_vat} onChange={(v) => setForm((f) => ({ ...f, price_ex_vat: v }))} /></div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Gekoppelde categorieën</Label>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    type="button"
                    key={c.category_id}
                    data-testid={`part-cat-${c.category_id}`}
                    onClick={() => toggleCat(c.category_id)}
                    className={`rounded border px-3 py-1 text-data transition-colors duration-150 ${
                      form.linked_categories.includes(c.category_id)
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-foreground hover:bg-muted"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            {error && <p className="text-body text-destructive sm:col-span-2" data-testid="part-editor-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => partGuard.requestClose(false)}>Annuleren</Button>
            <Button data-testid="part-save-button" onClick={submit} disabled={saving}>{saving ? "Bezig..." : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Page>
  );
}
